import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NocodeHeader from '@/components/nocode/NocodeHeader';
import CountdownTimer from '@/components/nocode/CountdownTimer';
import { motion } from 'framer-motion';
import GradientButton from '@/components/nocode/GradientButton';
import { toast } from 'sonner';
import { initiatePayment, CustomerData } from '@/services/paystackService';
import { supabase } from "@/integrations/supabase/client";

interface FormData {
  name: string;
  email: string;
  phone: string;
}

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    // Check if we have existing form data in database
    const checkExistingPayment = async (email: string) => {
      try {
        const { data, error } = await supabase
          .from('payments')
          .select('*')
          .eq('email', email)
          .eq('status', 'success')
          .maybeSingle();

        if (!error && data) {
          toast.info("You've already completed payment", {
            description: "Redirecting you to the thank you page."
          });

          navigate('/nocode/thank-you', {
            state: {
              name: data.name,
              email: data.email,
              phone: data.phone
            }
          });
          return true;
        }

        return false;
      } catch (error) {
        console.error("Error checking payment:", error);
        return false;
      }
    };

    // Get form data from location state
    if (location.state) {
      const userData = location.state as FormData;
      setFormData(userData);

      // Check if user has already paid
      checkExistingPayment(userData.email);
    } else {
      // If no form data, try from localStorage
      const storedData = sessionStorage.getItem('nocodeFormData');

      if (storedData) {
        const userData = JSON.parse(storedData) as FormData;
        setFormData(userData);

        // Check if user has already paid
        checkExistingPayment(userData.email);
      } else {
        // If no form data, redirect back to landing page
        navigate('/nocode');
      }
    }
  }, [location, navigate]);

  // Process payment with Paystack
  const handlePayment = async () => {
    try {
      setIsPaymentProcessing(true);

      const customerData: CustomerData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      };

      console.log('Initiating payment with data:', customerData);

      // Amount in kobo (33,960 Naira = 3,396,000 kobo)
      const authorizationUrl = await initiatePayment(customerData, {
        amount: 3396000,
        metadata: {
          custom_fields: [
            { name: 'product', value: 'AI-Powered No-Code Training' }
          ]
        }
      });

      console.log('Payment initiated, redirecting to:', authorizationUrl);

      // Store form data in sessionStorage for retrieval after payment (clears on browser close)
      sessionStorage.setItem('nocodeFormData', JSON.stringify(formData));

      // Redirect to Paystack checkout page
      window.location.href = authorizationUrl;
    } catch (error) {
      console.error('Payment processing error:', error);
      setIsPaymentProcessing(false);
      toast.error('Payment processing failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen w-full bg-white text-gray-800 font-poppins overflow-x-hidden">
      <NocodeHeader />

      <main className="container mx-auto px-4 py-24 md:py-32">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10 max-w-4xl mx-auto">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="font-montserrat font-bold text-2xl md:text-3xl text-center mb-2 bg-clip-text text-transparent bg-nocode-gradient">
              AI-Powered No-Code Training – Exclusive Access
            </h1>
            <p className="text-center text-gray-600 mb-8">
              You're just one step away from transforming your digital skills forever!
            </p>

            <div className="mb-8">
              <div className="flex justify-center mb-6">
                <CountdownTimer hours={24} />
              </div>

              <div className="p-6 bg-nocode-gray rounded-xl mb-6">
                <h3 className="font-montserrat font-semibold mb-4">Your Information</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      readOnly
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      readOnly
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      readOnly
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                </div>
              </div>

              <div className="p-6 bg-nocode-gray rounded-xl mb-8">
                <h3 className="font-montserrat font-semibold mb-4">Order Summary</h3>
                <div className="flex justify-between mb-4 pb-4 border-b border-gray-200">
                  <span>AI-Powered No-Code Training</span>
                  <span className="font-medium">₦33,960</span>
                </div>
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>₦33,960</span>
                </div>
              </div>

              <div className="text-center">
                <GradientButton
                  onClick={handlePayment}
                  disabled={isPaymentProcessing}
                  className="w-full md:w-auto py-4 px-8 text-lg"
                >
                  {isPaymentProcessing ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    "Secure Your Spot Now – ₦33,960"
                  )}
                </GradientButton>

                <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Secure payment via Paystack
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Payment;
